import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useSnackbar } from "notistack";
import { clientBackendPDF } from "configurations/axios";
import ContableTable from "../components/ContableTable";
import SelectorMesAnio from "../components/SelectorMesAnio";
import useContableDashboard from "../hooks/useContableDashboard";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 96px)",
    background: "#080f1f",
    padding: theme.spacing(3),
  },
  header: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
  },
  title: {
    color: "#f5f7fb",
    fontSize: 19,
    fontWeight: 900,
  },
  /* ── Stat cards ── */
  cards: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  card: {
    background: "#111827",
    border: "1px solid #1d3454",
    borderRadius: 10,
    flex: "1 1 0",
    padding: theme.spacing(2, 2.5),
  },
  cardLabel: {
    color: "#53647e",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1.2,
    marginBottom: theme.spacing(0.8),
    textTransform: "uppercase",
  },
  cardValue: {
    color: "#19b8f2",
    fontSize: 26,
    fontWeight: 900,
    lineHeight: 1,
  },
  cardSub: {
    color: "#53647e",
    fontSize: 11,
    marginTop: theme.spacing(0.5),
  },
  number: {
    color: "#bcd8f8",
    fontWeight: 900,
  },
  currency: {
    color: "#02dfb1",
    fontWeight: 700,
  },
  badge: {
    background: "#1a2e4a",
    borderRadius: 12,
    color: "#8bbcff",
    display: "inline-block",
    fontSize: 11,
    fontWeight: 900,
    padding: "2px 10px",
  },
  viewButton: {
    color: "#8bbcff",
    minWidth: 0,
    padding: theme.spacing(0.5),
  },
  /* ── Dialog ── */
  dialogPaper: {
    background: "#080f1f",
    color: "#f5f7fb",
  },
  dialogTitle: {
    background: "#0d172b",
    borderBottom: "1px solid #1d3454",
    padding: theme.spacing(2, 3),
  },
  dialogTitleText: {
    color: "#f5f7fb",
    fontSize: 17,
    fontWeight: 900,
  },
  dialogSubtitle: {
    color: "#8fb1d8",
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  dialogContent: {
    minHeight: "76vh",
    padding: theme.spacing(2),
  },
  ridePanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    height: "76vh",
    overflow: "hidden",
    position: "relative",
  },
  rideFrame: {
    background: "#ffffff",
    border: 0,
    height: "100%",
    width: "100%",
  },
  rideLoader: {
    alignItems: "center",
    color: "#acd0f6",
    display: "flex",
    gap: theme.spacing(1),
    height: "100%",
    justifyContent: "center",
  },
  dialogActions: {
    background: "#0d172b",
    borderTop: "1px solid #1d3454",
    padding: theme.spacing(1.5, 3),
  },
  closeButton: {
    color: "#acd0f6",
    textTransform: "none",
  },
}));

/* ── Helpers ── */
const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "$0.00";
  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getVoucherNumber = (voucher) => {
  const c = voucher.comprobanteElectronico || {};
  return [c.codigoSucursal, c.puntoFacturacion, c.numeroFactura]
    .filter(Boolean)
    .join("-");
};

/* ── Definición de columnas ─────────────────────────────────────────────────
   Agrega, quita o reordena entradas para personalizar la tabla.
   Cada columna: { key, label, render(row) => node, align? }           */
const buildColumns = (classes, openRide) => [
  {
    key: "numero",
    label: "Número NC",
    render: (row) => (
      <span className={classes.number}>{getVoucherNumber(row)}</span>
    ),
  },
  {
    key: "fecha",
    label: "Fecha emisión",
    render: (row) => formatDate(row.comprobanteElectronico?.fechaEmision),
  },
  {
    key: "cliente",
    label: "Cliente",
    render: (row) => row.receptor?.razonSocial || "Consumidor final",
  },
  // {
  //   key: "motivo",
  //   label: "Motivo",
  //   render: (row) => row.comprobanteElectronico?.motivoNotaCredito || "—",
  // },
  {
    key: "subtotal",
    label: "Subtotal",
    render: (row) => formatCurrency(row.comprobanteElectronico?.valorSubtotal),
  },
  {
    key: "iva",
    label: "ITBMS",
    render: (row) => formatCurrency(row.comprobanteElectronico?.valorItbms),
  },
  {
    key: "total",
    label: "Total",
    align: "right",
    render: (row) => (
      <span className={classes.currency}>
        {formatCurrency(row.comprobanteElectronico?.valorTotal)}
      </span>
    ),
  },
  {
    key: "estado",
    label: "Estado",
    render: (row) => (
      <span className={classes.badge}>
        {row.comprobanteElectronico?.mensajeAutorizacion || "Autorizado"}
      </span>
    ),
  },
  {
    key: "acciones",
    label: "Acciones",
    render: (row) => (
      <Tooltip title="Ver nota de crédito (PDF)">
        <Button className={classes.viewButton} onClick={() => openRide(row)}>
          <VisibilityIcon fontSize="small" />
        </Button>
      </Tooltip>
    ),
  },
];
/* ─────────────────────────────────────────────────────────────────────────── */

const NotasCreditoView = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: statsData,
    authorizedVouchers,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    mes,
    anio,
    onMesChange,
    onAnioChange,
  } = useContableDashboard({ voucherType: "04", useMonthFilter: true });

  const totalNC = statsData?.totalNC ?? "—";
  const valorNC =
    statsData?.valorNC != null
      ? `$${Number(statsData.valorNC).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "—";

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [rideUrl, setRideUrl] = useState("");
  const [rideLoading, setRideLoading] = useState(false);

  const openRide = async (voucher) => {
    const idComprobante = voucher.comprobanteElectronico?.idComprobanteElectronico;

    setSelectedVoucher(voucher);
    setPdfOpen(true);
    setRideLoading(true);

    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }

    try {
      const response = await clientBackendPDF.get(
        `/utilidades/generar/ride/${idComprobante}`,
      );
      const file = new Blob([response.data], { type: "application/pdf" });
      setRideUrl(window.URL.createObjectURL(file));
    } catch {
      enqueueSnackbar("No se pudo cargar el RIDE de la nota de crédito.", {
        variant: "error",
      });
    } finally {
      setRideLoading(false);
    }
  };

  const closeRide = () => {
    setPdfOpen(false);
    setSelectedVoucher(null);
    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }
  };

  const columns = buildColumns(classes, openRide);

  return (
    <div className={classes.root}>
      <SelectorMesAnio
        mes={mes}
        anio={anio}
        onMesChange={onMesChange}
        onAnioChange={onAnioChange}
      />

      {/* ── Cards NC ── */}
      <div className={classes.cards}>
        <div className={classes.card}>
          <Typography className={classes.cardLabel}>Notas de crédito emitidas</Typography>
          <Typography className={classes.cardValue}>{totalNC}</Typography>
          <Typography className={classes.cardSub}>comprobantes este mes</Typography>
        </div>
        <div className={classes.card}>
          <Typography className={classes.cardLabel}>Valor notas de crédito</Typography>
          <Typography className={classes.cardValue}>{valorNC}</Typography>
          <Typography className={classes.cardSub}>monto total este mes</Typography>
        </div>
      </div>

      <ContableTable
        columns={columns}
        rows={authorizedVouchers.comprobantes}
        totalElements={authorizedVouchers.totalElements}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={authorizedVouchers.loading}
        error={authorizedVouchers.error}
        emptyMessage="No existen notas de crédito para mostrar."
        loadingMessage="Cargando notas de crédito..."
      />

      {/* ── Visor PDF ── */}
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        fullWidth
        maxWidth="lg"
        onClose={closeRide}
        open={pdfOpen}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography className={classes.dialogTitleText}>
            Nota de Crédito{" "}
            {selectedVoucher ? getVoucherNumber(selectedVoucher) : ""}
          </Typography>
          <Typography className={classes.dialogSubtitle}>
            {selectedVoucher?.receptor?.razonSocial || "Consumidor final"}
          </Typography>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.ridePanel}>
            {rideLoading && (
              <div className={classes.rideLoader}>
                <CircularProgress color="inherit" size={24} />
                <span>Cargando RIDE...</span>
              </div>
            )}
            {!rideLoading && rideUrl && (
              <iframe
                className={classes.rideFrame}
                src={rideUrl}
                title="RIDE nota de crédito"
              />
            )}
            {!rideLoading && !rideUrl && (
              <div className={classes.rideLoader}>
                No se pudo mostrar el RIDE.
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button className={classes.closeButton} onClick={closeRide}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NotasCreditoView;
